import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ManageOrganizationAdminsComponent } from '../manage-organization-admins/manage-organization-admins.component';
import { TransferOrganizationOwnershipComponent } from '../transfer-organization-ownership/transfer-organization-ownership.component';
import { OrganizationGeneralSettingsComponent } from '../organization-general-settings/organization-general-settings.component';
import { OrganizationMessageSuppressionSettingsComponent } from '../organization-message-suppression-settings/organization-message-suppression-settings.component';
import { SplitNavContentDirective } from '../../../../core/directives/split-nav-content.directive';
import { SplitNavigationPanelComponent } from '../../../../parts/split-navigation-panel/split-navigation-panel.component';
import { StripeOrganizationOnboardingComponent } from '../../../stripe/components/stripe-organization-onboarding/stripe-organization-onboarding.component';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { OrganizationSettingModel } from '../../../../core/models/data/organization-setting.model';
import { OrganizationMessageSuppressionModel } from '../../../../core/models/data/organization-message-suppression.model';
import { SplitNavigationPanelConstants } from '../../../../core/constants/split-navigation-panel.constants';
import { NavItem } from '../../../../core/models/ui/profile-nav-item.model';
import { MessagePermission } from '../../../../core/enums/permissions.enums';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { OrganizationMessageSuppressionService } from '../../../../core/services/organization-message-suppression.service';

@Component({
  selector: 'ms-organization-settings',
  imports: [
      StripeOrganizationOnboardingComponent,
      SplitNavigationPanelComponent,
      SplitNavContentDirective,
      TransferOrganizationOwnershipComponent,
      ManageOrganizationAdminsComponent,
      OrganizationGeneralSettingsComponent,
      OrganizationMessageSuppressionSettingsComponent
    ],
  templateUrl: './organization-profile-settings.component.html'
})
export class OrganizationProfileSettingsComponent {
  private activatedRoute = inject(ActivatedRoute);
  private authorizationService = inject(AuthorizationService);
  private authorizationStateService = inject(AuthorizationStateService);
  private messageSuppressionService = inject(OrganizationMessageSuppressionService);

  public organization = input<OrganizationModel | undefined>(undefined);
  public organizationSettings = input<OrganizationSettingModel | undefined>(undefined);
  public reloadOrganization = output<void>();
  public organizationSettingsUpdated = output<OrganizationSettingModel>();
  public organizationUpdated = output<OrganizationModel>();

  public settingsPanelTitle = 'Organization Settings';
  public initialSubtab = signal<string | undefined>(this.activatedRoute.snapshot.queryParamMap.get('subtab') ?? undefined);

  private suppressionStatus = signal<OrganizationMessageSuppressionModel | undefined>(undefined);
  private activeTabId = signal<string>('');
  private lastLoadedSuppressionOrgId: number | undefined;

  public settingsNavItems = computed<NavItem[]>(() => {
    const items = SplitNavigationPanelConstants.ORGANIZATION_SETTINGS_NAV_ITEMS();
    const activeId = this.activeTabId();
    const suppression = this.suppressionStatus();
    const suppressionActive = !!suppression && (suppression.suppressMessages || suppression.overrideEmailRecipient);

    return items.map(item => {
      const next: NavItem = { ...item, active: activeId ? item.id === activeId : item.active };
      if (item.id === 'message-suppression' && suppressionActive) {
        next.badge = { label: 'Active', styleClass: 'bg-danger-subtle text-danger' };
      }
      return next;
    });
  });

  constructor() {
    effect(() => {
      const org = this.organization();
      const authState = this.authorizationStateService.authorizationState();
      const orgId = org?.organizationId;

      if (orgId !== this.lastLoadedSuppressionOrgId) {
        this.suppressionStatus.set(undefined);
        this.lastLoadedSuppressionOrgId = orgId;
      }

      if (!orgId || !authState) return;

      const canManage = this.authorizationService.hasPermission(
        authState,
        MessagePermission.ManageSuppressions,
        orgId
      );

      if (canManage) {
        this.loadSuppressionStatus(orgId);
      }
    });
  }

  private async loadSuppressionStatus(organizationId: number): Promise<void> {
    const result = await this.messageSuppressionService.getMessageSuppression(organizationId);
    if (organizationId !== this.lastLoadedSuppressionOrgId) {
      return;
    }
    if (result.success && result.data) {
      this.suppressionStatus.set(result.data);
    } else {
      this.suppressionStatus.set(undefined);
    }
  }

  public onSettingsTabChanged(tabId: string): void {
    this.activeTabId.set(tabId);
  }

  public onStripeOnboardingComplete(_stripeAccountId: string): void {
    this.reloadOrganization.emit();
  }

  public onStripeOnboardingExit(): void {
    // Handle any actions needed when onboarding is exited
  }

  public onGeneralSettingsUpdated(settings: OrganizationSettingModel): void {
    this.organizationSettingsUpdated.emit(settings);
  }

  public onOrganizationUpdated(organization: OrganizationModel): void {
    this.organizationUpdated.emit(organization);
  }

  public onSuppressionUpdated(model: OrganizationMessageSuppressionModel): void {
    this.suppressionStatus.set(model);
  }
}
